import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { ClinicGroupUser, UserRole } from '@magnetar/admin-api-interfaces'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { clinicGroupUsersService } from '@/services/clinic_group_users_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { EditClinicGroupUserDialog } from './EditClinicGroupUserDialog'

export const columns: ColumnDef<ClinicGroupUser>[] = [
  {
    accessorKey: 'clinic_group_user_id',
    header: 'ID',
    cell: ({ row }) => {
      const id: number = row.getValue('clinic_group_user_id')
      return (
        <Badge variant="outline" className="font-medium">
          {id}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'user',
    header: '使用者',
    cell: ({ row }) => {
      const user = row.original.user
      return (
        <div>
          <div className="font-medium">{user?.user_name || '-'}</div>
          <div className="text-sm text-muted-foreground">{user?.account || '-'}</div>
        </div>
      )
    },
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => {
      return row.original.user?.email || '-'
    },
  },
  {
    accessorKey: 'user_role',
    header: '角色',
    cell: function RoleSelectCell({ row, table }) {
      const userRole = row.original.user_role
      const { token } = useAuth()
      const [isUpdating, setIsUpdating] = useState(false)
      const meta = table.options.meta as {
        reloadData?: () => void
        availableRoles?: UserRole[]
      }

      const handleRoleChange = async (roleId: string) => {
        if (!token) return
        setIsUpdating(true)
        try {
          const result = await clinicGroupUsersService.updateClinicGroupUser(
            row.original.clinic_group_user_id,
            { user_role_id: roleId === 'none' ? null : Number(roleId) },
            token,
          )
          if (result.success) {
            toast.success('角色更新成功')
            meta?.reloadData?.()
          } else {
            toast.error(result.error?.message || '角色更新失敗')
          }
        } catch {
          toast.error('角色更新失敗')
        } finally {
          setIsUpdating(false)
        }
      }

      return (
        <Select
          value={userRole?.user_role_id?.toString() || 'none'}
          onValueChange={handleRoleChange}
          disabled={isUpdating}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="選擇角色" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">無角色</SelectItem>
            {meta?.availableRoles?.map((role) => (
              <SelectItem key={role.user_role_id} value={role.user_role_id.toString()}>
                {role.role_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    },
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const user = row.original
      const { token } = useAuth()
      const [isDeleting, setIsDeleting] = useState(false)
      const [showEditDialog, setShowEditDialog] = useState(false)
      const meta = table.options.meta as { reloadData?: () => void; availableRoles?: UserRole[] }

      const handleDelete = async () => {
        if (!token) return
        setIsDeleting(true)
        try {
          const result = await clinicGroupUsersService.deleteClinicGroupUser(
            user.clinic_group_user_id,
            token,
          )
          if (result.success) {
            toast.success('使用者已從集團中移除')
            meta?.reloadData?.()
          } else {
            toast.error(result.error?.message || '移除使用者失敗')
          }
        } catch {
          toast.error('移除使用者失敗')
        } finally {
          setIsDeleting(false)
        }
      }

      return (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowEditDialog(true)}>
            編輯
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={isDeleting}>
                移除
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>確定要移除此使用者？</AlertDialogTitle>
                <AlertDialogDescription>
                  此操作會將「{user.user?.user_name}」從集團中移除，但不會刪除使用者帳號。
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>取消</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>確定移除</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <EditClinicGroupUserDialog
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            clinicGroupUser={user}
            onSuccess={() => meta?.reloadData?.()}
          />
        </div>
      )
    },
  },
]
